import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FileStack, Mail, Sparkles } from 'lucide-react';
import apiService from '../../services/api';
import { BUILT_IN_SKILLS_FALLBACK, localizeSkillMeta } from '../../features/agent/skills/builtInSkills';
import type { AgentSkillMeta } from '../../features/agent/skills/types';
import { directionForLanguage } from '../../i18n/config';
import './SkillPicker.css';

const ICON_MAP: Record<string, React.ReactNode> = {
  'mail-merge': <Mail size={18} strokeWidth={2} aria-hidden />,
  'bank-of-changes': <FileStack size={18} strokeWidth={2} aria-hidden />,
  sparkles: <Sparkles size={18} strokeWidth={2} aria-hidden />,
};

interface SkillPickerProps {
  onSelect: (skill: AgentSkillMeta) => void;
}

/** Lists built-in guided workflows from the platform catalog (dashboard Workflows). */
export const SkillPicker: React.FC<SkillPickerProps> = ({ onSelect }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [workflows, setWorkflows] = React.useState<AgentSkillMeta[]>(BUILT_IN_SKILLS_FALLBACK);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.listAgentSkills(i18n.language);
        if (!cancelled && res.success && res.data?.skills?.length) {
          setWorkflows(res.data.skills.map(localizeSkillMeta));
        }
      } catch {
        /* use fallback */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  return (
    <div className="skill-picker" dir={chromeDir}>
      <p className="skill-picker-intro">{t('agent.workflowsIntro')}</p>
      {loading && <p className="skill-picker-loading">{t('agent.workflowsLoading')}</p>}
      <div className="skill-picker-grid">
        {workflows.map((workflow) => (
          <button
            key={workflow.id}
            type="button"
            className="skill-picker-card"
            onClick={() => onSelect(workflow)}
          >
            <span className="skill-picker-card-icon">
              {ICON_MAP[workflow.icon] ?? ICON_MAP.sparkles}
            </span>
            <span className="skill-picker-card-body">
              <span className="skill-picker-card-title">{workflow.name}</span>
              <span className="skill-picker-card-desc">{workflow.description}</span>
            </span>
            <span className="skill-picker-badge">{t('agent.builtIn')}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
